import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Box,
  Button,
  Input,
  VStack,
  Text,
  Heading,
  Flex,
  Stack,
  Link as ChakraLink,
  Field,
} from '@chakra-ui/react'
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom'
import { useEffect } from 'react'
import { Mail, Lock, AlertCircle, Info } from 'lucide-react'
import { useIntranetLogin } from '../hooks/useIntranetAuth'
import { useAuth } from '../hooks/useAuth'
import { useOnboardingStatus } from '../hooks/useOnboardingStatus'
import { supabase } from '@/lib/supabase'
import type { LoginCredentials } from '../types'

const loginSchema = z.object({
  email: z.string().email('Email invalide'),
  password: z.string().min(6, 'Le mot de passe doit contenir au moins 6 caractères'),
})

export function IntranetLoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const login = useIntranetLogin()
  const { data: auth, isLoading: authLoading } = useAuth()
  const { needsOnboarding, isLoading: onboardingLoading } = useOnboardingStatus()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>({
    resolver: zodResolver(loginSchema),
  })

  // Check for message parameter
  const message = searchParams.get('message')

  useEffect(() => {
    // Only redirect if already authenticated (e.g., page refresh)
    if (authLoading || onboardingLoading) {
      return
    }

    if (auth?.session && !login.isPending) {
      // Check if user needs onboarding
      if (needsOnboarding) {
        navigate('/app/onboarding')
        return
      }

      // Check if user has intranet access
      const hasIntranetRole = auth.residences.some((r) =>
        ['USER', 'MANAGER', 'ADMIN'].includes(r.role)
      )
      if (hasIntranetRole) {
        navigate('/app/dashboard')
      }
    }
  }, [auth, needsOnboarding, authLoading, onboardingLoading, navigate, login.isPending])

  const onSubmit = async (data: LoginCredentials) => {
    login.mutate(data, {
      onSuccess: async () => {
        // Wait for queries to refetch
        await new Promise(resolve => setTimeout(resolve, 100))

        // Get fresh auth data
        const { data: { session } } = await supabase.auth.getSession()

        if (!session) return

        // Check onboarding status
        const { data: userProfile } = await supabase
          .from('users')
          .select('agency_id')
          .eq('id', session.user.id)
          .single()

        const { data: userResidences } = await supabase
          .from('user_residences')
          .select('role')
          .eq('user_id', session.user.id)

        const hasAgency = !!userProfile?.agency_id
        const hasResidences = (userResidences?.length || 0) > 0

        // Navigate based on onboarding status
        if (hasAgency && !hasResidences) {
          navigate('/app/onboarding')
        } else if (userResidences?.some(r => ['USER', 'MANAGER', 'ADMIN'].includes(r.role))) {
          navigate('/app/dashboard')
        }
      }
    })
  }

  const getErrorMessage = (error: any) => {
    if (error?.message === 'INTRANET_ACCESS_DENIED') {
      return "Accès refusé. Vous n'avez pas les permissions nécessaires pour accéder à l'outil de gestion."
    }
    return 'Identifiants invalides. Veuillez réessayer.'
  }

  return (
    <Flex minH="100vh">
      {/* Left side - Login Form */}
      <Flex
        flex={1}
        bg="white"
        align="center"
        justify="center"
        px={{ base: 6, md: 12, lg: 16 }}
        py={12}
      >
        <Box w="full" maxW="440px">
          {/* Logo */}
          <Box mb={8} textAlign="center">
            <Flex direction="column" align="center" gap={3}>
              <img
                src="/assets/logo-compact-light.svg"
                alt="Kopro"
                style={{ height: '70px', width: 'auto' }}
              />
              <Box>
                <Heading size="xl" fontWeight="bold" color="gray.900">
                  Kopro
                </Heading>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Gestion de copropriétés
                </Text>
              </Box>
            </Flex>
          </Box>

          {/* Form Card */}
          <Box
            bg="white"
            p={8}
            borderRadius="2xl"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="lg"
          >
            {/* Heading */}
            <Stack gap={2} mb={8}>
              <Heading size="xl" fontWeight="bold" color="gray.900">
                Connexion
              </Heading>
              <Text color="gray.600" fontSize="md">
                Accédez à votre espace de gestion
              </Text>
            </Stack>

            {/* Info Message - Account Exists */}
            {message === 'account_exists' && (
              <Box
                w="full"
                p={4}
                mb={6}
                bg="blue.50"
                borderRadius="lg"
                borderLeft="4px solid"
                borderColor="blue.500"
              >
                <Flex align="start" gap={3}>
                  <Info size={20} color="#3b82f6" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <Box>
                    <Text color="blue.900" fontSize="sm" fontWeight="medium" mb={1}>
                      Compte existant détecté
                    </Text>
                    <Text color="blue.800" fontSize="sm">
                      Votre paiement a bien été enregistré. Un compte existe déjà avec cette adresse email. Veuillez vous connecter ci-dessous pour accéder à votre espace.
                    </Text>
                  </Box>
                </Flex>
              </Box>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack gap={5} align="stretch">
              {/* Email Field */}
              <Field.Root invalid={!!errors.email} w="full">
                <Field.Label fontSize="sm" fontWeight="medium" color="gray.700">
                  Adresse email
                </Field.Label>
                <Box position="relative" w='100%'>
                  <Box
                    position="absolute"
                    left={3}
                    top="50%"
                    transform="translateY(-50%)"
                    pointerEvents="none"
                  >
                    <Mail size={18} color="#a0aec0" />
                  </Box>
                  <Input
                    {...register('email')}
                    type="email"
                    placeholder="votre@email.com"
                    size="lg"
                    pl={10}
                    borderColor="gray.300"
                    _hover={{ borderColor: 'brand.400' }}
                    _focus={{ borderColor: 'brand.800', boxShadow: '0 0 0 1px var(--chakra-colors-brand-800)' }}
                  />
                </Box>
                {errors.email && (
                  <Field.ErrorText>
                    {errors.email.message}
                  </Field.ErrorText>
                )}
              </Field.Root>

              {/* Password Field */}
              <Field.Root invalid={!!errors.password} w="full">
                <Field.Label fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                  Mot de passe
                </Field.Label>
                <Box position="relative" w='100%'>
                  <Box
                    position="absolute"
                    left={3}
                    top="50%"
                    transform="translateY(-50%)"
                    pointerEvents="none"
                  >
                    <Lock size={18} color="#a0aec0" />
                  </Box>
                  <Input
                    {...register('password')}
                    type="password"
                    placeholder="••••••••"
                    size="lg"
                    pl={10}
                    borderColor="gray.300"
                    _hover={{ borderColor: 'brand.400' }}
                    _focus={{ borderColor: 'brand.800', boxShadow: '0 0 0 1px var(--chakra-colors-brand-800)' }}
                  />
                </Box>
                {errors.password && (
                  <Field.ErrorText>
                    {errors.password.message}
                  </Field.ErrorText>
                )}
              </Field.Root>

              {/* Forgot Password Link */}
              <Box w="full" textAlign="right">
                <ChakraLink
                  fontSize="sm"
                  color="brand.800"
                  fontWeight="medium"
                  _hover={{ color: 'brand.900', textDecoration: 'underline' }}
                >
                  Mot de passe oublié ?
                </ChakraLink>
              </Box>

              {/* Error Message */}
              {login.isError && (
                <Box
                  w="full"
                  p={3}
                  bg="red.50"
                  borderRadius="lg"
                  borderLeft="4px solid"
                  borderColor="red.500"
                >
                  <Flex align="start" gap={2}>
                    <AlertCircle size={18} color="#e53e3e" />
                    <Text color="red.700" fontSize="sm" flex={1}>
                      {getErrorMessage(login.error)}
                    </Text>
                  </Flex>
                </Box>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                w="full"
                bg="brand.800"
                color="white"
                fontWeight="semibold"
                loading={login.isPending}
                _hover={{ bg: 'brand.900' }}
                _active={{ bg: 'brand.900' }}
              >
                Se connecter
              </Button>

              {/* Link to Extranet */}
              <Box w="full" textAlign="center" pt={4} borderTop="1px solid" borderColor="gray.200">
                <Text fontSize="sm" color="gray.600">
                  Vous êtes copropriétaire ?{' '}
                  <RouterLink to="/app/extranet/login" style={{ textDecoration: 'none' }}>
                    <ChakraLink
                      color="brand.800"
                      fontWeight="medium"
                      _hover={{ color: 'brand.900', textDecoration: 'underline' }}
                    >
                      Accédez à l'extranet
                    </ChakraLink>
                  </RouterLink>
                </Text>
              </Box>
            </VStack>
          </form>
          </Box>
        </Box>
      </Flex>

      {/* Right side - Image/Illustration */}
      <Flex
        flex={1}
        bg="brand.500"
        align="center"
        justify="center"
        display={{ base: 'none', lg: 'flex' }}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top={0}
          right={0}
          bottom={0}
          left={0}
          bgGradient="to-br"
          gradientFrom="brand.600"
          gradientTo="brand.800"
          opacity={0.9}
        />
        <VStack gap={6} zIndex={1} px={12} textAlign="center">
          <Box mb={4}>
            <img
              src="/assets/logo inline dark.svg"
              alt="Kopro"
              style={{ height: '80px', width: 'auto' }}
            />
          </Box>
          <Heading size="2xl" color="white" fontWeight="bold" maxW="500px">
            Gérez vos copropriétés en toute simplicité
          </Heading>
          <Text fontSize="lg" color="whiteAlpha.900" maxW="450px">
            Solution complète pour syndics professionnels et gestionnaires de copropriétés
          </Text>
        </VStack>
      </Flex>
    </Flex>
  )
}
